import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthTokenService } from '@intranet/core/services/authtoken.service';
import { map } from 'rxjs/operators';
import {
    AddBankHolidayResult,
    AddBankHolidayYearResult,
    AddEntitlementResult,
    AddEntitlementsResult,
    AddHolidayResult,
    BankHoliday,
    CalculateUsageResult,
    DeleteBankHolidayResult,
    DeleteEntitlementResult,
    DeleteHolidayResult,
    DeleteNonWorkingDaysResult,
    GetBankHolidayResult,
    GetBankHolidaysResult,
    GetEntitlementsResult,
    GetHolidaysGridResult,
    GetHolidaysResult,
    GetHolidayUsageResult,
    GetNonWorkingDaysResult,
    Holiday,
    HolidayEntitlement,
    HolidayType,
    InitAddHolidayResult,
    NonWorkingDays,
    UpdateBankHolidayResult,
    AvailabilityResult,
    CalculateMultipleUsageResult,
    GetAllUserAvailabilityResult,
} from './holiday.types';
import { ResultBase } from '@intranet/core/types';

@Injectable({
    providedIn: 'root',
})
export class HolidayService {
    constructor(
        private http: AuthTokenService,
        @Inject('BASE_URL') private originUrl: string
    ) {}

    addBankHoliday(hol: BankHoliday): Observable<AddBankHolidayResult> {
        let body = {
            bankHoliday: hol,
        };
        return this.http
            .post(`${this.originUrl}api/Holiday/AddBankHoliday`, body)
            .pipe(map((r) => r as AddBankHolidayResult));
    }

    updateBankHoliday(hol: BankHoliday): Observable<UpdateBankHolidayResult> {
        let body = hol;
        return this.http
            .post(`${this.originUrl}api/Holiday/UpdateBankHoliday`, body)
            .pipe(map((r) => r as UpdateBankHolidayResult));
    }

    deleteBankHoliday(hol: BankHoliday): Observable<DeleteBankHolidayResult> {
        let body = {};
        return this.http
            .post(
                `${this.originUrl}api/Holiday/DeleteBankHoliday/${hol.bankHolidayId}`,
                body
            )
            .pipe(map((r) => r as DeleteBankHolidayResult));
    }

    getBankHolidays(): Observable<GetBankHolidaysResult> {
        return this.http
            .get(`${this.originUrl}api/Holiday/GetBankHolidays`)
            .pipe(map((r) => r as GetBankHolidaysResult));
    }

    getBankHoliday(id: number): Observable<GetBankHolidayResult> {
        return this.http
            .get(`${this.originUrl}api/Holiday/GetBankHoliday/${id}`)
            .pipe(map((r) => r as GetBankHolidayResult));
    }

    addBankHolidayYear(year: number): Observable<AddBankHolidayYearResult> {
        let body = {};
        return this.http
            .post(
                `${this.originUrl}api/Holiday/AddBankHolidayYear/${year}`,
                body
            )
            .pipe(map((r) => r as AddBankHolidayYearResult));
    }

    generateBankHolidays(year: number): Observable<GetBankHolidaysResult> {
        return this.http
            .get(`${this.originUrl}api/Holiday/GenerateBankHolidays/${year}`)
            .pipe(map((r) => r as GetBankHolidaysResult));
    }

    addEntitlements(
        entitlements: HolidayEntitlement[]
    ): Observable<AddEntitlementsResult> {
        let body = { entitlements: entitlements };
        return this.http
            .post(`${this.originUrl}api/Holiday/AddEntitlements`, body)
            .pipe(map((r) => r as AddEntitlementsResult));
    }

    getEntitlements(): Observable<GetEntitlementsResult> {
        return this.http
            .get(`${this.originUrl}api/Holiday/GetEntitlements`)
            .pipe(map((r) => r as GetEntitlementsResult));
    }

    addHoliday(hol: Holiday): Observable<AddHolidayResult> {
        let body = hol;
        return this.http
            .post(`${this.originUrl}api/Holiday/AddHoliday`, body)
            .pipe(map((r) => r as AddHolidayResult));
    }

    addMultipleHolidays(start: string, duration: number, userIds: string[], holidayType: HolidayType, isMorning: boolean, description: string): Observable<ResultBase> {
        let body = { start: start, duration: duration, userIds: userIds, holidayType: holidayType, isMorning: isMorning, description: description };
        return this.http
            .post(`${this.originUrl}api/Holiday/AddMultipleHolidays`, body)
            .pipe(map((r) => r as ResultBase));
    }

    updateHoliday(hol: Holiday): Observable<AddHolidayResult> {
        let body = hol;
        return this.http
            .post(`${this.originUrl}api/Holiday/UpdateHoliday`, body)
            .pipe(map((r) => r as AddHolidayResult));
    }

    getHolidays(date: string | null): Observable<GetHolidaysResult> {
        let url = `${this.originUrl}api/Holiday/GetHolidays`;
        if (date) {
            url += `/${date}`;
        }
        return this.http.get(url).pipe(map((r) => r as GetHolidaysResult));
    }

    getHolidaysForYear(year: number): Observable<GetHolidaysResult> {
        let url = `${this.originUrl}api/Holiday/GetHolidaysForYear/${year}`;
        return this.http.get(url).pipe(map((r) => r as GetHolidaysResult));
    }

    getHolidaysForUser(userid: string): Observable<GetHolidaysResult> {
        let url = `${this.originUrl}api/Holiday/GetHolidaysForUser/${userid}`;
        return this.http.get(url).pipe(map((r) => r as GetHolidaysResult));
    }

    getNonWorkingDaysForUser(userid: string): Observable<GetNonWorkingDaysResult> {
        let url = `${this.originUrl}api/Holiday/get-non-working-days/${userid}`;
        return this.http.get(url).pipe(map((r) => r as GetNonWorkingDaysResult));
    }

    deleteHoliday(hol: Holiday): Observable<DeleteHolidayResult> {
        let body = hol;
        return this.http
            .post(`${this.originUrl}api/Holiday/DeleteHoliday`, body)
            .pipe(map((r) => r as DeleteHolidayResult));
    }

    deleteNonWorkingDays(id: number): Observable<DeleteNonWorkingDaysResult> {
        let body = {};
        return this.http
            .post(`${this.originUrl}api/Holiday/delete-non-working-days/${id}`, body)
            .pipe(map((r) => r as DeleteHolidayResult));
    }

    addOrUpdateNonWorkingDays(
        nonWorkingDays: NonWorkingDays) : Observable<ResultBase> {
        let body = nonWorkingDays;
        return this.http
            .post(`${this.originUrl}api/Holiday/add-or-update-non-working-days`, body)
            .pipe(map((r) => r as ResultBase));
        }

    calculateUsage(hol: Holiday): Observable<CalculateUsageResult> {
        let body = hol;
        return this.http
            .post(`${this.originUrl}api/Holiday/CalculateUsage`, body)
            .pipe(map((r) => r as CalculateUsageResult));
    }

    calculateMultipleUsage(start: string, duration: number, userIds: string[], holidayType: HolidayType): Observable<CalculateMultipleUsageResult> {
        let body = { start: start, duration: duration, userIds: userIds, holidayType: holidayType };
        return this.http
            .post(`${this.originUrl}api/Holiday/CalculateMultipleUsage`, body)
            .pipe(map((r) => r as CalculateMultipleUsageResult));
    }

    initAddHoliday(
        year: number,
        id: string | undefined
    ): Observable<InitAddHolidayResult> {
        let url = `${this.originUrl}api/Holiday/InitAddHoliday/${year}`;
        if (id) {
            url += `/${id}`;
        }
        return this.http.get(url).pipe(map((r) => r as InitAddHolidayResult));
    }

    getHolidaysGrid(year?: number): Observable<GetHolidaysGridResult> {
        let url = `${this.originUrl}api/Holiday/GetHolidaysGrid`;
        if (year != null) {
            url += `?year=${year}`;
        }
        return this.http
            .get(url)
            .pipe(map((r) => r as GetHolidaysGridResult));
    }

    getHolidayUsageReport(year: number): Observable<GetHolidayUsageResult> {
        return this.http
            .get(`${this.originUrl}api/Holiday/GetHolidayUsageForYear?year=${year}`)
            .pipe(map((r) => r as GetHolidayUsageResult));
    }

    deleteEntitlement(
        ent: HolidayEntitlement
    ): Observable<DeleteEntitlementResult> {
        let body = ent;
        return this.http
            .post(`${this.originUrl}api/Holiday/DeleteEntitlement`, body)
            .pipe(map((r) => r as DeleteEntitlementResult));
    }

    addOrUpdateEntitlement(
        ent: HolidayEntitlement
    ): Observable<AddEntitlementResult> {
        let body = ent;
        return this.http
            .post(`${this.originUrl}api/Holiday/AddOrUpdateEntitlement`, body)
            .pipe(map((r) => r as AddEntitlementResult));
    }

    getAvailability(date?: string): Observable<AvailabilityResult> {
        const url = date
            ? `${this.originUrl}api/Holiday/Availability/${date}`
            : `${this.originUrl}api/Holiday/Availability`;
        return this.http
            .get(url)
            .pipe(map(r => r as AvailabilityResult));
    }

    getAllUserAvailability(date?: string): Observable<GetAllUserAvailabilityResult> {
        const url = date
            ? `${this.originUrl}api/Holiday/GetAllUserAvailability/${date}`
            : `${this.originUrl}api/Holiday/GetAllUserAvailability`;
        return this.http
            .get(url)
            .pipe(map(r => r as GetAllUserAvailabilityResult));
    }
}
